export function NumberWithCommas(x: string) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function deepEqualityCheck(value1: unknown, value2: unknown): boolean {
  // Handle primitive types and strict equality
  if (value1 === value2) return true;

  // Handle null or undefined explicitly
  if (value1 == null || value2 == null) return value1 === value2;

  // Ensure types match
  if (typeof value1 !== typeof value2) return false;

  // Handle arrays
  if (Array.isArray(value1) && Array.isArray(value2)) {
    if (value1.length !== value2.length) return false;
    return value1.every((item, index) =>
      deepEqualityCheck(item, value2[index])
    );
  }

  // Handle objects
  if (
    typeof value1 === 'object' &&
    typeof value2 === 'object' &&
    value1 !== null &&
    value2 !== null
  ) {
    const keys1 = Object.keys(value1);
    const keys2 = Object.keys(value2);

    if (keys1.length !== keys2.length) return false;

    return keys1.every((key) =>
      deepEqualityCheck(
        (value1 as Record<string, unknown>)[key],
        (value2 as Record<string, unknown>)[key]
      )
    );
  }

  // Handle Date objects
  if (value1 instanceof Date && value2 instanceof Date) {
    return value1.getTime() === value2.getTime();
  }

  // Handle RegExp objects
  if (value1 instanceof RegExp && value2 instanceof RegExp) {
    return value1.toString() === value2.toString();
  }

  //  Handle Maps
  if (value1 instanceof Map && value2 instanceof Map) {
    if (value1.size !== value2.size) return false;
    return Array.from(value1.entries()).every(
      ([key, val]) => value2.has(key) && deepEqualityCheck(val, value2.get(key))
    );
  }

  // Handle Set
  if (value1 instanceof Set && value2 instanceof Set) {
    if (value1.size !== value2.size) return false;
    return Array.from(value1).every((item) => value2.has(item));
  }

  // handle other types if needed in future

  // Default case: not equal
  return false;
}

export const getYearFromDate = (date?: string) => {
  if (!date) return new Date().getFullYear();
  const year = new Date(date).getFullYear();
  return Number.isNaN(year) ? new Date().getFullYear() : year;
};

export const getApiErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === 'string' && data.trim()) {
    return data;
  }
  return fallback;
};

export const getApiErrorStatus = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

export const resolveApiPath = (
  template: string,
  params: Record<string, string>
): string => {
  return Object.entries(params).reduce(
    (path, [key, value]) => path.replace(`{${key}}`, value),
    template
  );
};
